/**
 * Apply TrustArc Cookie Consent Manager preferences from localStorage `data.cookie_consent`
 * (partner / quote API shape: { functional: bool, marketing: bool }).
 *
 * Runs when all are true:
 *   - Path is `/quote` and the query string includes a non-empty `quote_session_id`
 *   - `data.cookie_consent` is present on the merged quote object in localStorage
 *
 * Sets `cmapi_cookie_privacy` (e.g. "permit 1,2,3") and `notice_behavior` (e.g. "expressed") so the
 * TrustArc notice script (often loaded via Adobe Launch) treats consent as already recorded.
 *
 * This file is loaded synchronously in the document head *before* Adobe Launch so cookies exist when
 * the CMP initializes. It also runs again from the quote products page after session merge.
 *
 * Meta tag `trustarc-consent-cookie-domain` (APP_CONSENT_DOMAIN): Domain= is only applied when it
 * matches `location.hostname` (localhost gets host-only cookies).
 */
import Utils from './util';

const TRUSTARC_PRIVACY_COOKIE = 'cmapi_cookie_privacy';
/** Companion cookie many TrustArc deployments expect so the banner does not re-prompt. */
const TRUSTARC_NOTICE_BEHAVIOR_COOKIE = 'notice_behavior';
const TRUSTARC_NOTICE_BEHAVIOR_VALUE = 'expressed,eu';
const TRUSTARC_NOTICE_GDPR_PREFS_COOKIE = 'notice_gdpr_prefs';
const TRUSTARC_NOTICE_PREFERENCES_COOKIE = 'notice_preferences';
const TRUSTARC_LOCAL_STORAGE_GDPR_PREFS = 'truste.eu.cookie.notice_gdpr_prefs';
const TRUSTARC_LOCAL_STORAGE_PREFERENCES = 'truste.eu.cookie.notice_preferences';
const BUCKET_REQUIRED = 1;
const BUCKET_FUNCTIONAL = 2;
const BUCKET_ADVERTISING = 3;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 395;
const NOTIFY_MAX_ATTEMPTS = 24;
const NOTIFY_INTERVAL_MS = 125;

function readQuoteDataObject() {
    const raw = Utils.getLocalStorageParsed('data', {});
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw;
    }
    return {};
}

function cookieHeaderPresent(name) {
    const prefix = name + '=';
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
        const segment = parts[i].trim();
        if (segment.startsWith(prefix)) {
            return true;
        }
    }
    return false;
}

function readCookieValue(name) {
    const prefix = name + '=';
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
        const segment = parts[i].trim();
        if (segment.startsWith(prefix)) {
            return segment.slice(prefix.length);
        }
    }
    return '';
}

function hasCmapiPrivacyCookie() {
    return cookieHeaderPresent(TRUSTARC_PRIVACY_COOKIE);
}

function hasNoticeBehaviorCookie() {
    return cookieHeaderPresent(TRUSTARC_NOTICE_BEHAVIOR_COOKIE);
}

function hasTrustArcConsentArtifacts() {
    const data = readQuoteDataObject();
    const noticePreferencesValue = buildNoticePreferencesValue(data.cookie_consent);

    return (
        readCookieValue(TRUSTARC_PRIVACY_COOKIE) !== '' &&
        readCookieValue(TRUSTARC_NOTICE_BEHAVIOR_COOKIE) === TRUSTARC_NOTICE_BEHAVIOR_VALUE &&
        readCookieValue(TRUSTARC_NOTICE_GDPR_PREFS_COOKIE) !== '' &&
        noticePreferencesValue != null &&
        readCookieValue(TRUSTARC_NOTICE_PREFERENCES_COOKIE) === noticePreferencesValue
    );
}

function getQuoteSessionIdFromUrl() {
    try {
        return (new URLSearchParams(window.location.search).get('quote_session_id') || '').trim();
    } catch (e) {
        return '';
    }
}

/**
 * Page/context where partner-supplied consent should be applied to TrustArc.
 */
function isTrustArcQuoteConsentContext() {
    const path = (window.location.pathname || '').replace(/\/$/, '') || '/';
    if (path !== '/quote') {
        return false;
    }
    return Boolean(getQuoteSessionIdFromUrl());
}

function isAffirmative(v) {
    return v === true || v === 'true' || v === 1 || v === '1';
}

function buildCmapiPrivacyValue(cookieConsent) {
    if (cookieConsent == null || typeof cookieConsent !== 'object') {
        return null;
    }
    const buckets = [BUCKET_REQUIRED];
    if (isAffirmative(cookieConsent.functional)) {
        buckets.push(BUCKET_FUNCTIONAL);
    }
    if (isAffirmative(cookieConsent.marketing)) {
        buckets.push(BUCKET_ADVERTISING);
    }
    return 'permit ' + buckets.join(',');
}

function buildNoticeGdprPrefsValue(cookieConsent) {
    if (cookieConsent == null || typeof cookieConsent !== 'object') {
        return null;
    }
    const categories = [0];
    if (isAffirmative(cookieConsent.functional)) {
        categories.push(1);
    }
    if (isAffirmative(cookieConsent.marketing)) {
        categories.push(2);
    }
    return categories.join(',') + ':';
}

function buildNoticePreferencesValue(cookieConsent) {
    if (cookieConsent == null || typeof cookieConsent !== 'object') {
        return null;
    }
    if (isAffirmative(cookieConsent.marketing)) {
        return '2:';
    }
    if (isAffirmative(cookieConsent.functional)) {
        return '1:';
    }
    return '0:';
}

function getConsentCookieDomainAttribute() {
    const meta = document.querySelector('meta[name="trustarc-consent-cookie-domain"]');
    if (!meta || !meta.content) {
        return '';
    }
    let d = meta.content.trim();
    if (!d) {
        return '';
    }
    if (!d.startsWith('.')) {
        d = '.' + d;
    }
    return d;
}

/**
 * Browsers reject Domain= on document.cookie when it does not match the current host * (e.g. domain=.uhcsafetrip-test.com while the page is localhost) — the assignment is ignored.
 */
function consentDomainMatchesCurrentHost(hostname, domainAttr) {
    if (!domainAttr) {
        return false;
    }
    const base = domainAttr.replace(/^\./, '').toLowerCase();
    const host = String(hostname || '').toLowerCase();
    if (!base || !host) {
        return false;
    }
    return host === base || host.endsWith('.' + base);
}

function setTrustArcFirstPartyCookie(name, value) {
    if (!name || value == null || value === '') {
        return;
    }
    const path = '/';
    const secure = window.location.protocol === 'https:';
    let cookie = name + '=' + value + ';path=' + path + ';max-age=' + COOKIE_MAX_AGE + ';SameSite=Lax';
    const domainAttr = getConsentCookieDomainAttribute();
    if (domainAttr && consentDomainMatchesCurrentHost(window.location.hostname, domainAttr)) {
        cookie += ';domain=' + domainAttr;
    }
    if (secure) {
        cookie += ';Secure';
    }
    document.cookie = cookie;
}

function setTrustArcConsentLocalStorage(name, value) {
    if (!name || value == null || value === '') {
        return;
    }
    const payload = {
        name,
        value,
        path: '/',
        expires: Date.now() + (COOKIE_MAX_AGE * 1000),
    };
    try {
        localStorage.setItem(name, JSON.stringify(payload));
    } catch (e) { /* ignore */ }
}

/**
 * Ask TrustArc / TRUSTe CMP to re-read consent from cookies (incl. cmapi_cookie_privacy).
 * Supports both legacy `truste.cma` and `PrivacyManagerAPI` (postMessage registration per TRUSTe docs).
 */
function registerPrivacyManagerPostMessage() {
    try {
        if (!window.PrivacyManagerAPI || !window.postMessage) {
            return;
        }
        const apiObject = {
            PrivacyManagerAPI: {
                action: 'getConsentDecision',
                timestamp: Date.now(),
                self: window.location.host,
            },
        };
        const target = window.top || window;
        target.postMessage(JSON.stringify(apiObject), '*');
    } catch (e) { /* ignore */ }
}

function pokeTrustArcConsentOnce() {
    const domainMeta = getConsentCookieDomainAttribute().replace(/^\./, '');
    const domain = domainMeta || window.location.hostname;

    try {
        if (window.truste && window.truste.cma && typeof window.truste.cma.callApi === 'function') {
            window.truste.cma.callApi('getGDPRConsentDecision', domain);
            return true;
        }
    } catch (e) { /* ignore */ }

    try {
        if (window.PrivacyManagerAPI && typeof window.PrivacyManagerAPI.callApi === 'function') {
            window.PrivacyManagerAPI.callApi('getGDPRConsentDecision', domain);
            return true;
        }
    } catch (e) { /* ignore */ }

    return false;
}

function notifyTrustArcConsentWithRetry() {
    let attempts = 0;
    registerPrivacyManagerPostMessage();

    function tick() {
        if (pokeTrustArcConsentOnce()) {
            try {
                window.dispatchEvent(new CustomEvent('safetrip:trustarc-consent-notified'));
            } catch (e) { /* ignore */ }
            return;
        }
        attempts += 1;
        if (attempts < NOTIFY_MAX_ATTEMPTS) {
            if (attempts === 1 || attempts % 4 === 0) {
                registerPrivacyManagerPostMessage();
            }
            setTimeout(tick, NOTIFY_INTERVAL_MS);
        }
    }

    tick();
}

function shouldDelayTrustArcAnalyticsLoad() {
    return isTrustArcQuoteConsentContext() && !hasTrustArcConsentArtifacts();
}

export function applyTrustArcConsentFromStorage() {
    if (!isTrustArcQuoteConsentContext()) {
        return;
    }

    const data = readQuoteDataObject();
    if (!Object.prototype.hasOwnProperty.call(data, 'cookie_consent')) {
        return;
    }

    const value = buildCmapiPrivacyValue(data.cookie_consent);
    const noticeGdprPrefsValue = buildNoticeGdprPrefsValue(data.cookie_consent);
    const noticePreferencesValue = buildNoticePreferencesValue(data.cookie_consent);
    if (value == null || noticeGdprPrefsValue == null || noticePreferencesValue == null) {
        return;
    }

    const needCmapi = readCookieValue(TRUSTARC_PRIVACY_COOKIE) !== value;
    const needNoticeBehavior = readCookieValue(TRUSTARC_NOTICE_BEHAVIOR_COOKIE) !== TRUSTARC_NOTICE_BEHAVIOR_VALUE;
    const needNoticeGdprPrefs = readCookieValue(TRUSTARC_NOTICE_GDPR_PREFS_COOKIE) !== noticeGdprPrefsValue;
    const needNoticePreferences = readCookieValue(TRUSTARC_NOTICE_PREFERENCES_COOKIE) !== noticePreferencesValue;

    if (needCmapi) {
        setTrustArcFirstPartyCookie(TRUSTARC_PRIVACY_COOKIE, value);
    }
    if (needNoticeBehavior) {
        setTrustArcFirstPartyCookie(TRUSTARC_NOTICE_BEHAVIOR_COOKIE, TRUSTARC_NOTICE_BEHAVIOR_VALUE);
    }
    if (needNoticeGdprPrefs) {
        setTrustArcFirstPartyCookie(TRUSTARC_NOTICE_GDPR_PREFS_COOKIE, noticeGdprPrefsValue);
    }
    if (needNoticePreferences) {
        setTrustArcFirstPartyCookie(TRUSTARC_NOTICE_PREFERENCES_COOKIE, noticePreferencesValue);
    }

    setTrustArcConsentLocalStorage(TRUSTARC_LOCAL_STORAGE_GDPR_PREFS, noticeGdprPrefsValue);
    setTrustArcConsentLocalStorage(TRUSTARC_LOCAL_STORAGE_PREFERENCES, noticePreferencesValue);

    notifyTrustArcConsentWithRetry();

    try {
        window.dispatchEvent(new CustomEvent('safetrip:trustarc-consent-synced', { detail: { value } }));
    } catch (e) { /* ignore */ }
}

window.applyTrustArcConsentFromStorage = applyTrustArcConsentFromStorage;
window.shouldDelayTrustArcAnalyticsLoad = shouldDelayTrustArcAnalyticsLoad;

/** Run immediately so cookies exist before Adobe Launch / TrustArc async bootstrap (head order). */
applyTrustArcConsentFromStorage();
